import React, { useEffect, useState } from 'react';
import { apiGet } from '../contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';

/** Course Name dropdown filled from the program catalog in the database.
 * `params` narrows it: { country, level } on the candidate form, { university_id }
 * when applying. Searched on the server (a country can have thousands of courses).
 * Typing a name that isn't listed can still be used, so universities without
 * program data never block the form. */
export default function CourseCombobox({ value, onChange, params = {}, placeholder = 'Select course...', className = '', testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const paramKey = JSON.stringify(params);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ ...JSON.parse(paramKey), q: search.trim() });
      setLoading(true);
      apiGet(`/universities/course-names?${qs}`, { signal: controller.signal })
        .then(r => setOptions(r.data || []))
        .catch(err => { if (err.code !== 'ERR_CANCELED') setOptions([]); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [open, search, paramKey]);

  const typed = search.trim();
  const pick = (v) => { onChange(v); setOpen(false); setSearch(''); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          id={testId}
          data-testid={testId}
          className={`w-full flex items-center justify-between rounded-md border border-contrast/10 bg-surface px-3 py-2 text-sm text-left focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 ${className}`}
        >
          <span className={`truncate ${value ? 'text-ink' : 'text-ink-3'}`}>{value || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 text-ink-3 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search courses..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{loading ? 'Loading...' : 'No matching course.'}</CommandEmpty>
            <CommandGroup>
              {typed && !options.some(o => o.toLowerCase() === typed.toLowerCase()) && (
                <CommandItem value={`__typed__${typed}`} onSelect={() => pick(typed)}>
                  Use "{typed}"
                </CommandItem>
              )}
              {options.map(o => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)}>
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${value === o ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{o}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
